import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import AddActorForm from '../components/AddActorForm';
import AddIndicatorForm from '../components/AddIndicatorForm';
import AddRelationshipForm from '../components/AddRelationshipForm';
import DeleteButton from '../components/DeleteButton';
import LinkExistingEntity from '../components/LinkExistingEntity';
import EntityDetailLayout from '../components/EntityDetailLayout';
import EntityPivotLink from '../components/EntityPivotLink';
import EvidenceTimezoneToggle from '../components/EvidenceTimezoneToggle';
import InvestigationGraph from '../components/InvestigationGraph';
import RelationshipTable from '../components/RelationshipTable';
import useEvidenceTimezone from '../hooks/useEvidenceTimezone';

const TABS = [
  { id: 'Overview', label: 'Overview' },
  { id: 'Relationships', label: 'Relationships' },
  { id: 'Timeline', label: 'Timeline' },
  { id: 'Actors', label: 'Actors' },
  { id: 'Personas', label: 'Personas' },
  { id: 'Evidence', label: 'Evidence' },
  { id: 'IOCs', label: 'IOCs' },
  { id: 'Interactions', label: 'Interactions' },
  { id: 'Notes', label: 'Notes' },
  { id: 'Reports', label: 'Reports' },
  { id: 'Audit Log', label: 'Audit Log' },
];

const CASE_TYPE_LABELS = {
  scam_investigation: 'Scam Investigation',
  dprk_it_worker_screening: 'DPRK IT Worker Screening',
  phishing_collection: 'Phishing Collection',
  crypto_fraud: 'Crypto Fraud',
  fake_recruiter: 'Fake Recruiter',
  malware_artifact: 'Malware Artifact',
  brand_impersonation: 'Brand Impersonation',
  general_threat_actor: 'General Threat Actor',
  client_submission: 'Client Submission',
  internal_research: 'Internal Research',
};

function CaseDetail() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { mode, setMode, format, localLabel, suffix } = useEvidenceTimezone();
  const [activeTab, setActiveTab] = useState('Overview');
  const [caseData, setCaseData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [actors, setActors] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [iocs, setIocs] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [notes, setNotes] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [noteContent, setNoteContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [graphOpen, setGraphOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [relRefreshKey, setRelRefreshKey] = useState(0);

  const isArchived = Boolean(caseData?.is_archived);

  const loadCase = async () => {
    setLoading(true);
    try {
      const [
        caseRes,
        timelineRes,
        actorsRes,
        personasRes,
        evidenceRes,
        iocsRes,
        interactionsRes,
        notesRes,
        auditRes,
      ] = await Promise.all([
        api.get(`/hdp/cases/${caseId}`),
        api.get(`/hdp/cases/${caseId}/timeline`),
        api.get(`/hdp/cases/${caseId}/actors`),
        api.get(`/hdp/personas?case_id=${caseId}`),
        api.get(`/hdp/cases/${caseId}/evidence`),
        api.get(`/hdp/cases/${caseId}/iocs`),
        api.get(`/hdp/cases/${caseId}/interactions`),
        api.get(`/hdp/cases/${caseId}/notes`),
        api.get('/hdp/audit-logs'),
      ]);
      setCaseData(caseRes.data);
      setTimeline(timelineRes.data.timeline || []);
      setActors(actorsRes.data.actors || []);
      setPersonas(personasRes.data.personas || []);
      setEvidence(evidenceRes.data.evidence || []);
      setIocs(iocsRes.data.iocs || []);
      setInteractions(interactionsRes.data.interactions || []);
      setNotes(notesRes.data.notes || []);
      setAuditLogs(
        (auditRes.data.audit_logs || []).filter((log) => log.resource_id === caseId)
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCase();
  }, [caseId]);

  const saveNote = async (e) => {
    e.preventDefault();
    if (!noteContent.trim() || isArchived) return;
    await api.post(`/hdp/cases/${caseId}/notes`, { content: noteContent });
    setNoteContent('');
    loadCase();
  };

  const downloadReport = async () => {
    const res = await api.get('/hdp/reports/intelligence', { params: { case_id: caseId } });
    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${caseData.case_number || caseId}-report.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const restoreCase = async () => {
    setRestoring(true);
    try {
      await api.patch(`/hdp/cases/${caseId}`, { restore: true });
      navigate('/cases');
    } catch (err) {
      console.error(err);
    } finally {
      setRestoring(false);
    }
  };

  const startEditing = () => {
    if (!caseData || isArchived) return;
    setEditForm({
      title: caseData.title || '',
      status: caseData.status || 'new',
      priority: caseData.priority || 'medium',
      risk_level: caseData.risk_level || 'medium',
      description: caseData.description || '',
      summary: caseData.summary || '',
      recommended_actions: caseData.recommended_actions || '',
      client_name: caseData.client_name || '',
      lead_investigator: caseData.lead_investigator || '',
      case_type: caseData.case_type || 'general_threat_actor',
    });
    setEditError('');
    setEditing(true);
  };

  const saveCaseEdit = async (event) => {
    event.preventDefault();
    if (isArchived || !editForm.title?.trim()) return;
    setSavingEdit(true);
    setEditError('');
    try {
      const res = await api.patch(`/hdp/cases/${caseId}`, {
        title: editForm.title.trim(),
        status: editForm.status,
        priority: editForm.priority,
        risk_level: editForm.risk_level,
        description: editForm.description,
        summary: editForm.summary,
        recommended_actions: editForm.recommended_actions,
        client_name: editForm.client_name,
        lead_investigator: editForm.lead_investigator,
        case_type: editForm.case_type,
      });
      setCaseData(res.data);
      setEditing(false);
    } catch (err) {
      setEditError(err.response?.data?.error || err.message || 'Failed to save case');
    } finally {
      setSavingEdit(false);
    }
  };

  const sidebar = caseData ? (
    <>
      <div className="entity-sidebar-section">
        <div className="entity-sidebar-title">Case pulse</div>
        <div className="entity-stat-list">
          <div><span>IOCs</span><strong>{caseData.indicator_count}</strong></div>
          <div><span>Evidence</span><strong>{caseData.evidence_count}</strong></div>
          <div><span>Actors</span><strong>{caseData.actor_count}</strong></div>
          <div><span>Personas</span><strong>{caseData.persona_count}</strong></div>
        </div>
      </div>
      <div className="entity-sidebar-section">
        <div className="entity-sidebar-title">Quick actions</div>
        <button type="button" className="btn btn-primary btn-sm" style={{ width: '100%', marginBottom: '8px' }} onClick={downloadReport}>
          Generate report
        </button>
        <Link to="/intelligence/search" className="btn btn-secondary btn-sm" style={{ width: '100%', display: 'block', textAlign: 'center' }}>
          Run search
        </Link>
      </div>
    </>
  ) : null;

  return (
    <>
    <EntityDetailLayout
      entityType="case"
      title={caseData?.title || 'Case'}
      subtitle={
        caseData
          ? `${caseData.case_number} · ${CASE_TYPE_LABELS[caseData.case_type] || caseData.case_type}`
          : ''
      }
      breadcrumbs={
        isArchived
          ? [
              { label: 'Archived Cases', to: '/cases?archived=1' },
              { label: caseData?.case_number || caseId },
            ]
          : [
              { label: 'Cases', to: '/cases' },
              { label: caseData?.case_number || caseId },
            ]
      }
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      loading={loading}
      notFound={!loading && !caseData ? 'Case not found.' : null}
      notFoundAction={<Link to={isArchived ? '/cases?archived=1' : '/cases'} className="btn btn-secondary">Back to Cases</Link>}
      headerActions={
        caseData && (
          <>
            {(activeTab === 'Evidence' || activeTab === 'IOCs') && (
              <EvidenceTimezoneToggle mode={mode} setMode={setMode} localLabel={localLabel} />
            )}
            {isArchived && <span className="archived-banner-pill">Archived · read-only</span>}
            <span className={`status-badge ${caseData.status}`}>{caseData.status}</span>
            <span className={`status-badge ${caseData.priority}`}>{caseData.priority} priority</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setGraphOpen(true)}>Graph</button>
            {isArchived ? (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={restoring}
                onClick={restoreCase}
              >
                {restoring ? 'Restoring...' : 'Restore case'}
              </button>
            ) : (
              <DeleteButton
                label="Archive case"
                className="btn btn-sm btn-danger"
                confirmMessage={`Archive case "${caseData.title}"? It will be removed from active lists.`}
                onDelete={async () => {
                  await api.delete(`/hdp/cases/${caseId}`);
                  navigate('/cases');
                }}
              />
            )}
          </>
        )
      }
      sidebar={sidebar}
    >
      {caseData && activeTab === 'Overview' && (
        <div className="card">
          <div className="card-body">
            <div className="stats-grid" style={{ marginBottom: 'var(--spacing-lg)' }}>
              <div className="stat-card"><div className="stat-label">Actors</div><div className="stat-value">{caseData.actor_count}</div></div>
              <div className="stat-card cyan"><div className="stat-label">Personas</div><div className="stat-value">{caseData.persona_count}</div></div>
              <div className="stat-card purple"><div className="stat-label">Evidence</div><div className="stat-value">{caseData.evidence_count}</div></div>
              <div className="stat-card orange"><div className="stat-label">IOCs</div><div className="stat-value">{caseData.indicator_count}</div></div>
            </div>

            {!isArchived && !editing && (
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={startEditing}>
                  Edit case
                </button>
              </div>
            )}

            {editing ? (
              <form onSubmit={saveCaseEdit}>
                <div className="form-group">
                  <label className="form-label">Title</label>
                  <input
                    className="form-input"
                    value={editForm.title}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                    required
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-input" value={editForm.status} onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}>
                      <option value="new">new</option>
                      <option value="open">open</option>
                      <option value="in_progress">in_progress</option>
                      <option value="pending">pending</option>
                      <option value="closed">closed</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Priority</label>
                    <select className="form-input" value={editForm.priority} onChange={(e) => setEditForm((prev) => ({ ...prev, priority: e.target.value }))}>
                      <option value="low">low</option>
                      <option value="medium">medium</option>
                      <option value="high">high</option>
                      <option value="critical">critical</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Risk</label>
                    <select className="form-input" value={editForm.risk_level} onChange={(e) => setEditForm((prev) => ({ ...prev, risk_level: e.target.value }))}>
                      <option value="low">low</option>
                      <option value="medium">medium</option>
                      <option value="high">high</option>
                      <option value="critical">critical</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Case type</label>
                    <select className="form-input" value={editForm.case_type} onChange={(e) => setEditForm((prev) => ({ ...prev, case_type: e.target.value }))}>
                      {Object.entries(CASE_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-input" rows={3} value={editForm.description} onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Summary</label>
                  <textarea className="form-input" rows={3} value={editForm.summary} onChange={(e) => setEditForm((prev) => ({ ...prev, summary: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Recommended actions</label>
                  <textarea className="form-input" rows={2} value={editForm.recommended_actions} onChange={(e) => setEditForm((prev) => ({ ...prev, recommended_actions: e.target.value }))} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Client</label>
                    <input className="form-input" value={editForm.client_name} onChange={(e) => setEditForm((prev) => ({ ...prev, client_name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Lead investigator</label>
                    <input className="form-input" value={editForm.lead_investigator} onChange={(e) => setEditForm((prev) => ({ ...prev, lead_investigator: e.target.value }))} />
                  </div>
                </div>
                {editError && <p style={{ color: 'var(--danger, #f66)' }}>{editError}</p>}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" className="btn btn-primary" disabled={savingEdit}>
                    {savingEdit ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)} disabled={savingEdit}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <p style={{ color: 'var(--text-secondary)' }}>{caseData.description || 'No description provided.'}</p>
                <div style={{ marginTop: 'var(--spacing-lg)' }}>
                  <p><strong>Client:</strong> {caseData.client_name || '—'}</p>
                  <p><strong>Lead:</strong> {caseData.lead_investigator || '—'}</p>
                  <p><strong>Risk:</strong> {caseData.risk_level}</p>
                  <p><strong>Summary:</strong> {caseData.summary || '—'}</p>
                  <p><strong>Recommended actions:</strong> {caseData.recommended_actions || '—'}</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {caseData && activeTab === 'Relationships' && (
        <div className="card">
          <div className="card-body">
            {!isArchived && (
              <AddRelationshipForm
                sourceType="case"
                sourceId={caseId}
                sourceLabel={caseData.title}
                caseId={caseId}
                onCreated={() => setRelRefreshKey((key) => key + 1)}
              />
            )}
            <RelationshipTable
              entityType="case"
              entityId={caseId}
              allowDelete={!isArchived}
              refreshKey={relRefreshKey}
            />
          </div>
        </div>
      )}

      {caseData && activeTab === 'Timeline' && (
        <div className="card">
          <div className="card-body">
            {timeline.length === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>No timeline events yet.</p>
            ) : (
              timeline.map((event) => (
                <div key={event.event_id} style={{ borderLeft: '2px solid var(--neon-cyan)', paddingLeft: '16px', marginBottom: '16px' }}>
                  <div style={{ color: 'var(--neon-green)', fontSize: '12px' }}>{event.event_type}</div>
                  <div style={{ fontWeight: 600 }}>{event.title}</div>
                  {event.description && <div style={{ color: 'var(--text-secondary)' }}>{event.description}</div>}
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                    {event.timestamp && new Date(event.timestamp).toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {caseData && activeTab === 'Actors' && (
        <div className="card"><div className="card-body">
          {!isArchived && (
            <div className="entity-attach-panel">
              <LinkExistingEntity
                mode="actor-to-case"
                caseId={caseId}
                onLinked={(actor) => {
                  setActors((prev) => {
                    if (prev.some((row) => row.actor_id === actor.actor_id)) return prev;
                    return [actor, ...prev];
                  });
                  setCaseData((prev) => prev ? { ...prev, actor_count: (prev.actor_count || 0) + 1 } : prev);
                  setRelRefreshKey((key) => key + 1);
                }}
              />
              <div className="entity-attach-divider">or create new</div>
              <AddActorForm
                caseId={caseId}
                onCreated={(actor) => {
                  setActors((prev) => [actor, ...prev]);
                  setCaseData((prev) => prev ? { ...prev, actor_count: (prev.actor_count || 0) + 1 } : prev);
                  setRelRefreshKey((key) => key + 1);
                }}
              />
            </div>
          )}
          {actors.length === 0 ? <p style={{ color: 'var(--text-muted)' }}>No actors linked.</p> : (
            <table className="data-table"><thead><tr><th>Name</th><th>Type</th><th>Risk</th><th>Status</th><th /></tr></thead>
            <tbody>{actors.map((a) => (
              <tr key={a.actor_id}>
                <td>
                  <EntityPivotLink type="actor" id={a.actor_id} label={a.display_name} showBadge={false} />
                </td>
                <td>{a.actor_type}</td>
                <td>{a.risk_level}</td>
                <td>{a.status}</td>
                <td>
                  {!isArchived && (
                  <DeleteButton
                    confirmMessage={`Remove actor "${a.display_name}"?`}
                    onDelete={async () => {
                      await api.delete(`/hdp/actors/${a.actor_id}`);
                      setActors((prev) => prev.filter((row) => row.actor_id !== a.actor_id));
                    }}
                  />
                  )}
                </td>
              </tr>
            ))}</tbody></table>
          )}
        </div></div>
      )}

      {caseData && activeTab === 'Personas' && (
        <div className="card"><div className="card-body">
          {personas.length === 0 ? <p style={{ color: 'var(--text-muted)' }}>No personas linked.</p> : (
            <table className="data-table"><thead><tr><th>Alias</th><th>Type</th><th>Status</th><th>Platform</th></tr></thead>
            <tbody>{personas.map((p) => (
              <tr key={p.persona_id}>
                <td><EntityPivotLink type="persona" id={p.persona_id} label={p.alias} showBadge={false} /></td>
                <td>{p.persona_type}</td>
                <td>{p.status}</td>
                <td>{p.platform || '—'}</td>
              </tr>
            ))}</tbody></table>
          )}
        </div></div>
      )}

      {caseData && activeTab === 'Evidence' && (
        <div className="card"><div className="card-body">
          {evidence.length === 0 ? <p style={{ color: 'var(--text-muted)' }}>No evidence linked.</p> : (
            <table className="data-table"><thead><tr><th>Title</th><th>Type</th><th>Scan</th><th>Collected ({suffix})</th><th /></tr></thead>
            <tbody>{evidence.map((e) => (
              <tr key={e.evidence_id}>
                <td><EntityPivotLink type="evidence" id={e.evidence_id} label={e.title} showBadge={false} /></td>
                <td>{e.evidence_type}</td>
                <td>{e.scan_status}</td>
                <td className="timestamp-cell">{format(e.collected_at)}</td>
                <td>
                  <DeleteButton
                    confirmMessage={`Delete evidence "${e.title}"?`}
                    onDelete={async () => {
                      await api.delete(`/hdp/evidence/${e.evidence_id}`);
                      setEvidence((prev) => prev.filter((row) => row.evidence_id !== e.evidence_id));
                    }}
                  />
                </td>
              </tr>
            ))}</tbody></table>
          )}
        </div></div>
      )}

      {caseData && activeTab === 'IOCs' && (
        <div className="card"><div className="card-body">
          {!isArchived && (
            <div className="entity-attach-panel">
              <LinkExistingEntity
                mode="indicator-to-case"
                caseId={caseId}
                onLinked={(ioc) => {
                  setIocs((prev) => {
                    if (prev.some((row) => row.indicator_id === ioc.indicator_id)) return prev;
                    return [ioc, ...prev];
                  });
                  setCaseData((prev) => prev ? { ...prev, indicator_count: (prev.indicator_count || 0) + 1 } : prev);
                  setRelRefreshKey((key) => key + 1);
                }}
              />
              <div className="entity-attach-divider">or create new</div>
              <AddIndicatorForm
                caseId={caseId}
                onCreated={(ioc) => {
                  setIocs((prev) => [ioc, ...prev]);
                  setCaseData((prev) => prev ? { ...prev, indicator_count: (prev.indicator_count || 0) + 1 } : prev);
                  setRelRefreshKey((key) => key + 1);
                }}
              />
            </div>
          )}
          {iocs.length === 0 ? <p style={{ color: 'var(--text-muted)' }}>No IOCs linked.</p> : (
            <table className="data-table"><thead><tr><th>Type</th><th>Value</th><th>Confidence</th><th>Last Seen ({suffix})</th><th /></tr></thead>
            <tbody>{iocs.map((i) => (
              <tr key={i.indicator_id}>
                <td>{i.ioc_type}</td>
                <td><EntityPivotLink type="indicator" id={i.indicator_id} label={i.value} showBadge={false} /></td>
                <td>{i.confidence}</td>
                <td className="timestamp-cell">{format(i.last_seen)}</td>
                <td>
                  {!isArchived && (
                  <DeleteButton
                    confirmMessage={`Delete indicator "${i.value}"?`}
                    onDelete={async () => {
                      await api.delete(`/hdp/iocs/${i.indicator_id}`);
                      setIocs((prev) => prev.filter((row) => row.indicator_id !== i.indicator_id));
                    }}
                  />
                  )}
                </td>
              </tr>
            ))}</tbody></table>
          )}
        </div></div>
      )}

      {caseData && activeTab === 'Interactions' && (
        <div className="card"><div className="card-body">
          {interactions.length === 0 ? <p style={{ color: 'var(--text-muted)' }}>No interactions logged yet.</p> : (
            interactions.map((item) => (
              <div key={item.interaction_id} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                  <div>
                    <div style={{ color: 'var(--neon-cyan)', fontSize: '12px' }}>{item.channel} · {item.direction}</div>
                    <div style={{ fontWeight: 600 }}>{item.subject || 'Interaction'}</div>
                    <div style={{ color: 'var(--text-secondary)' }}>{item.body || item.summary}</div>
                  </div>
                  <DeleteButton
                    confirmMessage="Delete this interaction?"
                    onDelete={async () => {
                      await api.delete(`/hdp/interactions/${item.interaction_id}`);
                      setInteractions((prev) => prev.filter((row) => row.interaction_id !== item.interaction_id));
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </div></div>
      )}

      {caseData && activeTab === 'Notes' && (
        <div className="card">
          <div className="card-body">
            {!isArchived && (
            <form onSubmit={saveNote} style={{ marginBottom: 'var(--spacing-lg)' }}>
              <textarea className="form-input" rows={4} value={noteContent} onChange={(e) => setNoteContent(e.target.value)} placeholder="Add analyst note..." />
              <button type="submit" className="btn btn-primary" style={{ marginTop: 'var(--spacing-sm)' }}>Add Note</button>
            </form>
            )}
            {isArchived && (
              <p style={{ color: 'var(--text-muted)', marginBottom: 'var(--spacing-lg)' }}>
                Notes are read-only on archived cases.
              </p>
            )}
            {notes.map((note) => (
              <div key={note.note_id} style={{ marginBottom: '12px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                  <div>
                    <div>{note.content}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{note.created_at && new Date(note.created_at).toLocaleString()}</div>
                  </div>
                  {!isArchived && (
                  <DeleteButton
                    confirmMessage="Delete this note?"
                    onDelete={async () => {
                      await api.delete(`/hdp/cases/${caseId}/notes/${note.note_id}`);
                      setNotes((prev) => prev.filter((row) => row.note_id !== note.note_id));
                    }}
                  />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {caseData && activeTab === 'Reports' && (
        <div className="card"><div className="card-body">
          <p style={{ color: 'var(--text-secondary)' }}>Generate a client-ready intelligence summary for this case.</p>
          <button className="btn btn-primary" onClick={downloadReport}>Download JSON Report</button>
        </div></div>
      )}

      {caseData && activeTab === 'Audit Log' && (
        <div className="card"><div className="card-body">
          <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
            Audit entries are immutable for transparency. Deletions of cases, evidence, and indicators are recorded here and cannot be removed.
          </p>
          {auditLogs.length === 0 ? <p style={{ color: 'var(--text-muted)' }}>No audit entries for this case yet.</p> : (
            auditLogs.map((log) => (
              <div key={log.id} style={{ marginBottom: '12px' }}>
                <div style={{ color: 'var(--neon-green)' }}>{log.action}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{log.timestamp && new Date(log.timestamp).toLocaleString()}</div>
              </div>
            ))
          )}
        </div></div>
      )}
    </EntityDetailLayout>

    <InvestigationGraph
      open={graphOpen}
      onClose={() => setGraphOpen(false)}
      entityType="case"
      entityId={caseId}
      title={caseData?.title}
    />
    </>
  );
}

export default CaseDetail;
